import { Link } from 'react-router-dom'

import type { Listing } from '@/api/listings'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import {
  Card,
  DataTable,
  EmptyState,
  formatCurrency,
  formatPercent,
  Icon,
  LoadingBlock,
  PacingBadge,
  type Column,
  type FormatOptions,
} from '@/design'
import { drillDownHref } from '@/lib/drilldown'
import { formatDateKey } from '@/lib/filters'
import { useFilterSearch, useGlobalFilters } from '@/lib/useGlobalFilters'

import { useListings, useMoney } from './hooks'
import { ListingStatusBadge } from './ListingStatusBadge'
import './listings.css'

function columns(money: FormatOptions, performanceHref: (listing: Listing) => string | null): Column<Listing>[] {
  return [
    {
      key: 'title',
      header: 'Listing',
      sortValue: (row) => row.copy?.title ?? row.prize.prizeName,
      cell: (row) => (
        <span className="lst-cell-stack">
          <span>{row.copy?.title ?? row.prize.prizeName}</span>
          <span className="lst-cell-sub">{row.prize.prizeCategory}</span>
        </span>
      ),
    },
    { key: 'status', header: 'Status', sortValue: (row) => row.status, cell: (row) => <ListingStatusBadge status={row.status} /> },
    {
      key: 'ticketPrice',
      header: 'Ticket price',
      align: 'end',
      sortValue: (row) => row.prize.ticketPrice,
      cell: (row) => formatCurrency(row.prize.ticketPrice, money),
    },
    {
      key: 'drawDate',
      header: 'Draw date',
      align: 'end',
      sortValue: (row) => row.prize.drawDate,
      cell: (row) => formatDateKey(row.prize.drawDate, true),
    },
    {
      key: 'sellThrough',
      header: 'Sell-through',
      align: 'end',
      sortValue: (row) => row.sellThrough ?? -1,
      cell: (row) => (row.sellThrough === null ? '—' : formatPercent(row.sellThrough, 0)),
    },
    {
      key: 'pacing',
      header: 'Pacing',
      cell: (row) => {
        const href = performanceHref(row)
        if (!row.pacing) return <span className="lst-cell-sub">Not linked</span>
        return href ? (
          <Link to={href} className="lst-pacing-link">
            <PacingBadge status={row.pacing} />
          </Link>
        ) : (
          <PacingBadge status={row.pacing} />
        )
      },
    },
  ]
}

/** Every competition listing, its review status, and — where it pairs with a live competition — its pacing (pivot doc §6 Stage 3). */
export function ListingsPage() {
  const notLive = useNotLivePage('listings')
  const listings = useListings()
  const money = useMoney()
  const { filters } = useGlobalFilters()
  const search = useFilterSearch()

  const performanceHref = (listing: Listing): string | null =>
    listing.competitionEntity ? drillDownHref({ path: `/analytics/competitions/${encodeURIComponent(listing.competitionEntity.id)}` }, filters) : null

  // No "New listing" either: nothing a viewer drafts here would be kept.
  if (notLive) return <NotLivePage />

  return (
    <Card
      title="Listings"
      subtitle="Drafted, reviewed and published competition listings."
      busy={listings.isFetching}
      actions={
        <Link className="ci-button" data-variant="primary" to={{ pathname: '/competitions/listings/new', search }}>
          <Icon name="sparkle" size={14} />
          New listing
        </Link>
      }
    >
      {listings.isPending ? <LoadingBlock rows={5} /> : null}
      {listings.isError ? <EmptyState variant="outage" compact description="Listings could not be loaded." /> : null}
      {listings.data ? (
        <DataTable
          caption="Competition listings"
          columns={columns(money, performanceHref)}
          rows={listings.data.data}
          rowKey={(row) => row.id}
          rowHref={(row) => `/competitions/listings/${row.id}${search}`}
          initialSort={{ key: 'drawDate', direction: 'asc' }}
          empty={
            <EmptyState
              variant="no-data"
              compact
              title="No listings yet"
              description="Create your first listing to draft content and choose where it gets published."
            />
          }
        />
      ) : null}
    </Card>
  )
}
